import { useCallback, useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs
} from '@material-ui/core'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import MessagesAPI from '../../components/MessagesAPI'
import ProtectedRoute from '../../components/ProtectedRoute'

import {
  Signal
} from 'lucide-react'
import ConfirmationModal from '../../components/ConfirmationModal'
import ConnectionCard from '../../components/ConnectionCard'
import ConnectionAlertModal from '../../components/ConnectionAlertModal'
import PrivacyModal from '../../components/PrivacyModal'
import PasskeyModal from '../../components/PasskeyModal'
import TabPanel from '../../components/TabPanel'
import { WhatsAppsContext } from '../../context/WhatsApp/WhatsAppsContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './styles'
import { ButtonAddNew } from '../../components/ButtonAddNew'

const Connections = () => {
  const classes = useStyles()

  const { whatsApps, loading, fetchWhatsApps } = useContext(WhatsAppsContext)
  const [isNewConnection, setIsNewConnection] = useState(false)
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false)
  const [alertModalOpen, setAlertModalOpen] = useState(false)
  const [autoOpenQrForId, setAutoOpenQrForId] = useState(null)
  const [passkeyModalOpen, setPasskeyModalOpen] = useState(false)
  const [passkeyInitialToken, setPasskeyInitialToken] = useState('')
  const [connectorReady, setConnectorReady] = useState(false)
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const confirmationModalInitialState = {
    action: '',
    title: '',
    message: '',
    whatsAppId: '',
    open: false
  }
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState
  )
  const [tab, setTab] = useState('connections')
  const location = useLocation()
  const history = useHistory()

  useEffect(() => {
    if (location.state && location.state.openQrModalFor && !loading) {
      const whatsAppName = location.state.openQrModalFor;
      const whatsApp = whatsApps.find(w => w.name === whatsAppName);
      if (whatsApp) {
        handleRequestNewQrCode(whatsApp.id);
        setAutoOpenQrForId(whatsApp.id);
        history.replace({ ...history.location, state: undefined });
      }
    }
  }, [location.state, whatsApps, history, loading]);


  const handleTabChange = (event, newValue) => {
    setTab(newValue)
  }

  const handleStartWhatsAppSession = async whatsAppId => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`)
    } catch (err) {
      toastError(err)
    }
  }

  const handleRequestNewQrCode = async whatsAppId => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`)
    } catch (err) {
      toastError(err)
    }
  }

  const handleAddNewConnection = () => {
    setIsNewConnection(true)
  }

  const handleCancelNewConnection = () => {
    setIsNewConnection(false)
  }

  const handleSaveConnection = () => {
    setIsNewConnection(false)
    fetchWhatsApps()
  }

  const handleOpenPasskeyModal = whatsApp => {
    setSelectedWhatsApp(whatsApp)
    setPasskeyModalOpen(true)
  }

  const handleResetPasskeySession = async whatsAppId => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}/reset`)
    } catch (err) {
      toastError(err)
    }
  }

  const handleClosePasskeyModal = useCallback(() => {
    setSelectedWhatsApp(null)
    setPasskeyInitialToken('')
    setPasskeyModalOpen(false)
  }, [setPasskeyModalOpen, setSelectedWhatsApp, setPasskeyInitialToken])

  const handleOpenPrivacyWhatsApp = whatsApp => {
    setSelectedWhatsApp(whatsApp)
    setPrivacyModalOpen(true)
  }

  const handleClosePrivacyWhatsAppModal = useCallback(() => {
    setSelectedWhatsApp(null)
    setPrivacyModalOpen(false)
  }, [setPrivacyModalOpen, setSelectedWhatsApp])

  const handleOpenConnectionAlertWhatsApp = whatsApp => {
    setSelectedWhatsApp(whatsApp)
    setAlertModalOpen(true)
  }

  const handleCloseConnectionAlertWhatsAppModal = useCallback(() => {
    setSelectedWhatsApp(null)
    setAlertModalOpen(false)
  }, [setAlertModalOpen, setSelectedWhatsApp])

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === 'disconnect') {
      setConfirmModalInfo({
        action: action,
        title: i18n.t('connections.confirmationModal.disconnectTitle'),
        message: i18n.t('connections.confirmationModal.disconnectMessage'),
        whatsAppId: whatsAppId
      })
    }

    if (action === 'delete') {
      setConfirmModalInfo({
        action: action,
        title: i18n.t('connections.confirmationModal.deleteTitle'),
        message: i18n.t('connections.confirmationModal.deleteMessage'),
        whatsAppId: whatsAppId
      })
    }
    setConfirmModalOpen(true)
  }

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === 'disconnect') {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`)
      } catch (err) {
        toastError(err)
      }
    }

    if (confirmModalInfo.action === 'delete') {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`)
        toast.success(i18n.t('connections.toasts.deleted'))
      } catch (err) {
        toastError(err)
      }
    }

    setConfirmModalInfo(confirmationModalInitialState)
  }

  const refreshWhatsApp = async whatsApp => {
    try {
      await api.get(`/whatsappsession/refresh/${whatsApp.id}`)
    } catch (err) {
      toastError(err)
    }
  }

  const createConnectionAnnouncement = async whatsApp => {
    try {
      await api.post(`/whatsapp/${whatsApp.id}/create-announcement`)
      toast.success('Anúncio de conexão desconectada criado com sucesso!')
    } catch (err) {
      toastError(err)
    }
  }

  return (
    <ProtectedRoute
      requiredRoles={['super', 'admin', 'conexões']}
      fallbackPath="/tickets"
    >
      <MainContainer>
        <ConfirmationModal
          title={confirmModalInfo.title}
          open={confirmModalOpen}
          onClose={setConfirmModalOpen}
          onConfirm={handleSubmitConfirmationModal}
        >
          {confirmModalInfo.message}
        </ConfirmationModal>

        <PasskeyModal
          open={passkeyModalOpen}
          onClose={handleClosePasskeyModal}
          captureToken={passkeyInitialToken}
          onConnectorReady={() => setConnectorReady(true)}
          whatsAppId={!privacyModalOpen && !alertModalOpen && selectedWhatsApp?.id}
        />
        <PrivacyModal
          open={privacyModalOpen}
          onClose={handleClosePrivacyWhatsAppModal}
          whatsAppId={!alertModalOpen && selectedWhatsApp?.id}
        />
        <ConnectionAlertModal
          open={alertModalOpen}
          onClose={handleCloseConnectionAlertWhatsAppModal}
          whatsAppId={!privacyModalOpen && selectedWhatsApp?.id}
        />
        <MainHeader
          title={i18n.t('connections.title')}
          description="Configure as conexões para integrar o WhatsApp ao sistema e iniciar o atendimento automatizado."
          showTutorial
        >
          <MainHeaderButtonsWrapper>
            <ButtonAddNew
              onClick={handleAddNewConnection}
              startIcon={<Signal size={20} />}
              label="Nova Conexão"
            />
          </MainHeaderButtonsWrapper>
        </MainHeader>
        <Paper className={classes.mainPaper} variant="outlined">
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="on"
            variant="scrollable"
            onChange={handleTabChange}
            className={classes.tab}
          >
            <Tab
              label={'Conexões'}
              value={'connections'}
              className={classes.tabHeader}
            />
            <Tab
              label={'Documentação'}
              value={'empty'}
              className={classes.tabHeader}
            />
          </Tabs>
          <TabPanel
            className={classes.container}
            value={tab}
            name={'connections'}
          >
            <div className={classes.cardContainer}>
              {loading ? (
                <CircularProgress />
              ) : (
                <>
                  {isNewConnection && (
                    <ConnectionCard
                      isNew
                      whatsApp={{}}
                      onSave={handleSaveConnection}
                      onCancel={handleCancelNewConnection}
                    />
                  )}
                  {whatsApps.map(whatsApp => (
                    <ConnectionCard
                      key={whatsApp.id}
                      whatsApp={whatsApp}
                      onUpdate={fetchWhatsApps}
                      onDelete={handleOpenConfirmationModal}
                      onPasskey={handleOpenPasskeyModal}
                      onResetPasskey={handleResetPasskeySession} onPrivacy={handleOpenPrivacyWhatsApp}
                      onConnectionAlert={handleOpenConnectionAlertWhatsApp}
                      onStartSession={handleStartWhatsAppSession}
                      onRequestNewQrCode={handleRequestNewQrCode}
                      onCreateAnnouncement={createConnectionAnnouncement}
                      onRefresh={refreshWhatsApp}
                      onDisconnect={handleOpenConfirmationModal}
                      autoOpenQrCode={Number(whatsApp.id) === Number(autoOpenQrForId)}
                    />
                  ))}
                </>
              )}
            </div>
          </TabPanel>
          <TabPanel className={classes.container} value={tab} name={'empty'}>
            <ProtectedRoute
              requiredRoles={['super', 'admin']}
              fallbackPath="/tickets"
            >
              <MessagesAPI />
            </ProtectedRoute>
          </TabPanel>
        </Paper>
      </MainContainer>
    </ProtectedRoute>
  )
}

export default Connections